/**
 * Validation utilities for SPV Business Profile
 * Replaces dangerous eval() usage with safe validation
 * Consolidates validation logic into testable functions
 */

import { camelCaseToNormalCase } from "../../../Utils/formatter";

export interface SPVBizProfileErrors {
  companyName: string;
  registrationNumber: string;
  dateOfIncorporation: string;
  legalStructure: string;
  legalStructureOthers?: string;
  industryGroup: string;
  institutionType?: string;
  industryOthers?: string;
  country: string;
  address: string;
  postalcode: string;
  website: string;
  firstName: string;
  lastName: string;
}

export interface SPVBizProfileData {
  companyName: string;
  registrationNumber: string;
  dateOfIncorporation: string;
  legalStructure: string;
  legalStructureOthers: string;
  industryGroup: string;
  institutionType: string;
  industryOthers: string;
  country: string;
  address: string;
  postalcode: string;
  website: string;
  firstName: string;
  lastName: string;
  isOtherLegalStructure: boolean;
}

/**
 * Create empty error object
 */
export const createEmptyErrors = (): SPVBizProfileErrors => ({
  companyName: "",
  registrationNumber: "",
  dateOfIncorporation: "",
  legalStructure: "",
  legalStructureOthers: "",
  industryGroup: "",
  institutionType: "",
  industryOthers: "",
  country: "",
  address: "",
  postalcode: "",
  website: "",
  firstName: "",
  lastName: "",
});

/**
 * Validate required field
 * SAFE replacement for dangerous eval() usage
 */
const validateRequiredField = (
  value: string | undefined | null,
  fieldName: string
): string => {
  if (!value || value.trim() === "") {
    return camelCaseToNormalCase(fieldName) + " is required";
  }
  return "";
};

/**
 * Validate all SPV business profile fields
 * Returns errors object with validation messages
 *
 * This is a SAFE replacement for the dangerous eval() pattern
 * that was previously used in the component
 */
export const validateSPVBizProfile = (
  data: SPVBizProfileData
): { isValid: boolean; errors: SPVBizProfileErrors } => {
  const errors = createEmptyErrors();
  let isValid = true;

  // Validate required fields
  const requiredFields: (keyof Pick<
    SPVBizProfileData,
    | "companyName"
    | "registrationNumber"
    | "dateOfIncorporation"
    | "legalStructure"
    | "industryGroup"
    | "country"
    | "address"
    | "postalcode"
    | "website"
    | "firstName"
    | "lastName"
  >)[] = [
    "companyName",
    "registrationNumber",
    "dateOfIncorporation",
    "legalStructure",
    "industryGroup",
    "country",
    "address",
    "postalcode",
    "website",
    "firstName",
    "lastName",
  ];

  for (const field of requiredFields) {
    const error = validateRequiredField(data[field], field);
    if (error) {
      errors[field] = error;
      isValid = false;
    }
  }

  // Validate conditional: legal structure others
  if (data.isOtherLegalStructure && !data.legalStructureOthers) {
    errors.legalStructureOthers = "Legal Structure Specification is required";
    isValid = false;
  }

  // Validate institution type
  if (!data.institutionType) {
    errors.institutionType = "Industry is required";
    isValid = false;
  }

  // Override error messages for better UX
  if (errors.country) {
    errors.country = "Country of Incorporation is required";
  }

  if (errors.postalcode) {
    errors.postalcode = "Postal Code is required";
  }

  if (errors.website) {
    errors.website = "Company Website is required";
  }

  return { isValid, errors };
};
